import React, { useState, useMemo } from 'react';
import { Autocomplete, Paper, TextField } from '@mui/material';

import { formatNumber } from '../../utils/formatNumber';
import './PlanSelect.css';

/**
 * Searchable, grouped plan picker shared by the admin workspaces filter and the custom plan modal.
 * Plans are grouped by plan type, with chips in the dropdown header to narrow by type and billing interval.
 * `valueKey` selects which plan field is reported through onChange ('name' for filters, '_id' for forms).
 */
const PlanSelect = ({ plans, value, onChange, valueKey = 'name', placeholder = 'All Plans', className = '' }) => {
  const [groupFilters, setGroupFilters] = useState({ type: '', interval: '' });
  const [isOpen, setIsOpen] = useState(false);

  const planOptions = useMemo(() => sortPlans(Array.isArray(plans) ? plans : []), [plans]);
  const typeOptions = useMemo(() => PLAN_TYPE_ORDER.filter(type => planOptions.some(plan => getPlanType(plan) === type)), [planOptions]);
  const intervalOptions = useMemo(() => INTERVAL_ORDER.filter(interval => planOptions.some(plan => plan['interval'] === interval)), [planOptions]);
  const selectedPlan = useMemo(() => planOptions.find(plan => plan[valueKey] === value) || null, [planOptions, value, valueKey]);

  // Reason: the selected plan is always kept in the list so MUI never holds a value that is missing from options
  const filteredOptions = useMemo(() => planOptions.filter(plan =>
    (selectedPlan && plan['_id'] === selectedPlan['_id']) ||
    ((!groupFilters['type'] || getPlanType(plan) === groupFilters['type']) && (!groupFilters['interval'] || plan['interval'] === groupFilters['interval']))
  ), [planOptions, groupFilters, selectedPlan]);

  const updateGroupFilters = (patch) => setGroupFilters(prev => ({ ...prev, ...patch }));

  return (
    <Autocomplete
      className={`plan-select ${className}`}
      size="small"
      options={filteredOptions}
      value={selectedPlan}
      onChange={(event, plan) => onChange(plan ? plan[valueKey] : '')}
      getOptionLabel={getPlanLabel}
      isOptionEqualToValue={(option, selected) => option['_id'] === selected['_id']}
      groupBy={(plan) => getPlanTypeLabel(getPlanType(plan))}
      noOptionsText="No plans match"
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      PaperComponent={PlanSelectPaper}
      // Reason: global rules like `.MuiPaper-root.MuiPaper-elevation ul li { padding !important }` (Dashboard/Campaign index.css) restyle any ul/li in MUI popups, so the listbox, groups and options all render as divs
      ListboxComponent="div"
      slotProps={{
        popper: { className: 'plan-select-popper', placement: 'bottom-start' },
        paper: { groupFilters, typeOptions, intervalOptions, resultCount: filteredOptions.length, onGroupFilterChange: updateGroupFilters }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={isOpen ? 'Search plans...' : placeholder}
          InputProps={{ ...params['InputProps'], startAdornment: <i className="plan-select-search-icon fa fa-search" aria-hidden="true" /> }}
        />
      )}
      renderGroup={(params) => (
        <div key={params['key']} className="plan-select-group">
          <div className="plan-select-group-header">
            {params['group']}
            <span className="plan-select-group-count">{React.Children.count(params['children'])}</span>
          </div>
          <div className="plan-select-group-list">{params['children']}</div>
        </div>
      )}
      renderOption={(props, plan) => {
        const { key, ...optionProps } = props;
        return (
          <div key={plan['_id']} {...optionProps} className={`${optionProps['className']} plan-select-option`}>
            <div className="plan-select-option-info">
              <span className="plan-select-option-name">{plan['displayName'] || plan['name']}</span>
              <span className="plan-select-option-price">${formatNumber(plan['price'])}/{INTERVAL_SHORT[plan['interval']] || plan['interval']}</span>
            </div>
            <div className="plan-select-option-tags">
              {plan['isDraft'] && <span className="plan-select-option-state">Draft</span>}
              {plan['isActive'] === false && !plan['isDraft'] && <span className="plan-select-option-state">Inactive</span>}
              <span className={`plan-select-option-interval plan-select-interval-${plan['interval']}`}>{INTERVAL_LABELS[plan['interval']] || plan['interval']}</span>
            </div>
          </div>
        );
      }}
    />
  );
};

/**
 * Dropdown paper with type/interval filter chips above the option list.
 * Reason: onMouseDown preventDefault keeps the input focused so clicking chips does not close the dropdown.
 */
const PlanSelectPaper = ({ children, className, groupFilters = {}, typeOptions = [], intervalOptions = [], resultCount = 0, onGroupFilterChange, ...rest }) => (
  <Paper
    {...rest}
    className={`${className || ''} plan-select-paper`}
    // Reason: the popper is a React portal, so synthetic clicks bubble through the React tree to Reactstrap Modal's handleBackdropClick, whose DOM contains() check fails for portal content and closes the modal
    onClick={(event) => { event.stopPropagation(); rest['onClick']?.(event); }}
    onMouseDown={(event) => { event.stopPropagation(); rest['onMouseDown']?.(event); }}
  >
    <div className="plan-select-popup-header" onMouseDown={(event) => event.preventDefault()}>
      {typeOptions.length > 1 && (
        <div className="plan-select-chip-row">
          <span className="plan-select-chip-label">Type</span>
          <div className="plan-select-chip-group">
            <button type="button" className={`plan-select-chip ${!groupFilters['type'] ? 'active' : ''}`} onClick={() => onGroupFilterChange?.({ type: '' })}>All</button>
            {typeOptions.map(type => (
              <button key={type} type="button" className={`plan-select-chip ${groupFilters['type'] === type ? 'active' : ''}`} onClick={() => onGroupFilterChange?.({ type })}>
                {PLAN_TYPE_CHIP_LABELS[type] || type}
              </button>
            ))}
          </div>
        </div>
      )}
      {intervalOptions.length > 1 && (
        <div className="plan-select-chip-row">
          <span className="plan-select-chip-label">Billing</span>
          <div className="plan-select-chip-group">
            <button type="button" className={`plan-select-chip ${!groupFilters['interval'] ? 'active' : ''}`} onClick={() => onGroupFilterChange?.({ interval: '' })}>All</button>
            {intervalOptions.map(interval => (
              <button key={interval} type="button" className={`plan-select-chip ${groupFilters['interval'] === interval ? 'active' : ''}`} onClick={() => onGroupFilterChange?.({ interval })}>
                {INTERVAL_LABELS[interval] || interval}
              </button>
            ))}
          </div>
        </div>
      )}
      <span className="plan-select-result-count">{resultCount} plan{resultCount === 1 ? '' : 's'}</span>
    </div>
    {children}
  </Paper>
);

const INTERVAL_ORDER = ['month', 'quarter', 'year'];
const INTERVAL_LABELS = { month: 'Monthly', quarter: 'Quarterly', year: 'Yearly' };
const INTERVAL_SHORT = { month: 'mo', quarter: 'qtr', year: 'yr' };
const PLAN_TYPE_ORDER = ['business', 'agency', 'doneForYou'];
const PLAN_TYPE_GROUP_LABELS = { business: 'Business Plans', agency: 'Agency Plans', doneForYou: 'Done For You Plans' };
const PLAN_TYPE_CHIP_LABELS = { business: 'Business', agency: 'Agency', doneForYou: 'Done For You' };
const PLAN_TIER_ORDER = ['free', 'starter', 'growth', 'professional', 'enterprise'];

/** Resolves the plan type, falling back to the displayText suffix for older API responses without planType. */
function getPlanType(plan) {
  return plan['planType'] || (plan['displayText'] || '').split(' - ').pop() || 'business';
}

function getPlanTypeLabel(type) {
  return PLAN_TYPE_GROUP_LABELS[type] || `${type.charAt(0).toUpperCase()}${type.slice(1)} Plans`;
}

/** Composes the searchable input label so plans without a server displayText still match on price/type. */
function getPlanLabel(plan) {
  return plan['displayText'] || `${plan['displayName'] || plan['name']} - $${plan['price']}/${INTERVAL_SHORT[plan['interval']] || plan['interval']} - ${getPlanType(plan)}`;
}

/** Orders plans by type, then tier, then billing interval, then price so groupBy renders contiguous groups. */
function sortPlans(plans) {
  const typeRank = (plan) => { const index = PLAN_TYPE_ORDER.indexOf(getPlanType(plan)); return index === -1 ? PLAN_TYPE_ORDER.length : index; };
  const tierRank = (plan) => { const tier = (plan['displayName'] || plan['name'] || '').split(/[\s_-]/)[0].toLowerCase(); const index = PLAN_TIER_ORDER.indexOf(tier); return index === -1 ? PLAN_TIER_ORDER.length : index; };
  const intervalRank = (plan) => { const index = INTERVAL_ORDER.indexOf(plan['interval']); return index === -1 ? INTERVAL_ORDER.length : index; };
  return [...plans].sort((a, b) => typeRank(a) - typeRank(b) || tierRank(a) - tierRank(b) || intervalRank(a) - intervalRank(b) || (a['price'] || 0) - (b['price'] || 0));
}

export default PlanSelect;
